/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import { sortBy } from 'lodash-es';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { RenderParam } from 'ksnm-common-ui/lib/Table2/Table.interface';
import type {
  Filter as TableFilter,
  ResponseDataRow,
  ResponseDataRows,
} from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { auditStore } from '@stores/auditStore';
import { constantStore } from '@stores/constantStore';
import { passingControlStore } from '@stores/passingControlStore';
import type { ReportingBlockDto, ReportingPackageDto } from '@src/api';

import { useSetGroupColumnWidth } from '../useSetGroupColumnWidth';
import { ActionButton } from './ActionButton';
import { AddControlVersionsDrawer } from './AddControlVersionsDrawer';
import { fieldFormat, getColumns } from './columns';
import { InfoDrawer } from './InfoDrawer';
import { usePushFromDataControl } from './usePushFromDataControl';

const { TABLE_NAMES } = constantStore;

const loadFromSource = (dataSource: any[], topRow: ResponseDataRow = {}, tlevel = 1): ResponseDataRows => {
  const output: ResponseDataRows = [];
  for (const row of dataSource) {
    const currentRow: any = {
      ...topRow,
      ...row,
      children: undefined,
      [`name${tlevel}`]: row.name,
    };

    if (tlevel === 4) {
      currentRow.packageInfo = row;
    }

    if (row.children && row.children.length > 0) {
      loadFromSource(row.children, currentRow, tlevel + 1).forEach((subrow) => output.push(subrow));
    } else if (row.controlVersions && row.controlVersions.length > 0) {
      loadFromSource(
        row.controlVersions.map((item) => ({ ...item, name: item.ctrlVersExt })),
        currentRow,
        tlevel + 1,
      ).forEach((subrow) => output.push(subrow));
    } else {
      output.push(currentRow);
    }
  }

  return sortBy(output, [
    'reportPackageYear',
    'reportGroupCode',
    'periodCode',
    (i) => Number(i.reportPackageCorrection ?? 0),
    'name5',
    'name6',
  ]);
};

interface IPassingControl {
  tabs: JSX.Element;
  filters: { [p: string]: string | undefined };
  organizationCode: string;
}

export const PassingControl = observer(({ tabs, filters, organizationCode }: IPassingControl): JSX.Element => {
  const [hardFilter, setHardFilter] = useState<TableFilter[][]>([]);
  const [selectEntity, setSelectEntity] = useState<(ReportingPackageDto & ReportingBlockDto) | null>(null);
  const [openAddControlDrawer, toggleAddControlDrawer] = useReducer((state) => !state, false);

  const { controlReports, clear, getControlReportByOrganizationCode, setSelected } = passingControlStore;

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.PASSING_CONTROL });

  useEffect(() => {
    auditStore.createAuditData({
      moduleName: 'Calculator',
      serviceName: 'Control_procedure_execution',
      operationName: 'View_kp_tree',
      message: 'Просмотр списка Контрольных процедур',
    });
  }, []);

  useEffect(() => {
    (async () => {
      if (organizationCode) {
        await getControlReportByOrganizationCode(organizationCode);
        update();
      }
    })();
  }, [getControlReportByOrganizationCode, organizationCode, update]);

  useEffect(() => {
    const newFilter: TableFilter[] = [];

    if (filters.organization) {
      newFilter.push({ name: 'name1', type: FILTER.EQ, values: [filters.organization], filterIsInverted: false });
    }

    if (filters.year) {
      newFilter.push({ name: 'name2', type: FILTER.EQ, values: [filters.year], filterIsInverted: false });
    }

    if (filters.group) {
      newFilter.push({ name: 'name3', type: FILTER.EQ, values: [filters.group], filterIsInverted: false });
    }

    if (filters.package) {
      newFilter.push({ name: 'name4', type: FILTER.EQ, values: [filters.package], filterIsInverted: false });
    }
    setHardFilter([newFilter]);
  }, [filters, organizationCode]);

  useEffect(() => {
    if (controlReports.length > 0) update();
  }, [controlReports.length, update]);

  const localData = useMemo<LocalDataSource>(
    () => ({
      data: loadFromSource(controlReports),
      fieldFormat: fieldFormat,
    }),
    [controlReports],
  );

  usePushFromDataControl(update);
  useSetGroupColumnWidth(TABLE_NAMES.PASSING_CONTROL);

  useEffect(() => () => clear(), [clear]);

  const emptyCellGroupRender = useCallback(({ data }: RenderParam) => data.name6, []);

  const columns = useMemo(() => getColumns(localData.data), [localData.data]);

  const onRowClick = useCallback(
    (row) => {
      if (row.level === 4) {
        const out = localData.data.find((item) => item.name4 === row.name4)?.packageInfo as ReportingPackageDto;
        setSelectEntity(out);
        return;
      }

      if (row.reportPackageCodeCm) {
        setSelectEntity(row);
      }
    },
    [localData.data],
  );

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      const rows = await getLocalRowsByCheckBoxStatus({ tableRequest: tr, localData, selectRows: sr });
      setSelected({ selectRows: sr, rows });
    },
    [localData, setSelected],
  );

  return (
    <>
      <Table
        tableName={TABLE_NAMES.PASSING_CONTROL}
        tableTitle={tabs}
        externalControl={externalControl}
        resource=""
        keyField="id"
        key={key}
        columns={columns}
        hardFilter={hardFilter}
        localDataSource={localData}
        showAggregateRow={false}
        cellHeight={40}
        onRowClick={onRowClick}
        emptyCellGroupRender={emptyCellGroupRender}
        doNotShowGroupPanel
        disableChangeGroup
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={
          <ActionButton
            toggleAddControlDrawer={toggleAddControlDrawer}
            organizationCode={organizationCode}
            update={update}
          />
        }
      />

      <InfoDrawer selectEntity={selectEntity} setSelectEntity={setSelectEntity} />
      <AddControlVersionsDrawer
        visible={openAddControlDrawer}
        onClose={toggleAddControlDrawer}
        organizationCode={organizationCode}
      />
    </>
  );
});
